import classNames from 'classnames';
import React from 'react';
import RichText from '@/components/atoms/rich-text/rich-text';
import Typography from '@/components/atoms/typography/typography';
import Button from '@/components/molecules/button/button';
import Lightbox from '@/components/utilities/lightbox/lightbox';
import { useI18n } from '@/libraries/contexts/i18n-context';
import useCurrencyFormatter from '@/libraries/formatters/use-currency-formatter';
import styles from './price-details.module.scss';

export interface PriceDetailsProps {
  isOpen: boolean;
  priceDetails: Content.Components['PriceDetailsDto'];
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  url?: string;
}

const PriceDetails = ({
  priceDetails: {
    headline,
    includedProducts,
    preHeadline,
    roomPrices,
    roomSummaries,
    text,
    totalPrice,
  },
  isOpen,
  setIsOpen,
  url,
}: PriceDetailsProps) => {
  const currencyFormatter = useCurrencyFormatter();
  const { t } = useI18n();

  if (!isOpen) return null;

  return (
    <Lightbox isOpen={isOpen} onClose={() => setIsOpen(false)}>
      <div className={styles.component}>
        <div className={styles.header}>
          <Typography className={styles.subHeadline} tagStyle="labelMedium">
            {preHeadline}
          </Typography>

          <Typography className={styles.headline} tagStyle="displayMedium">
            {headline}
          </Typography>

          <RichText text={text} />
        </div>

        <ul className={styles.roomSummaries}>
          {roomSummaries.map((roomSummary, roomSummaryIndex) => (
            <li key={roomSummaryIndex} className={styles.roomSummary}>
              <Typography tagStyle="bodyMedium">
                {`${roomSummary.roomCount} x ${roomSummary.roomTypeName}`}
              </Typography>
            </li>
          ))}
        </ul>

        <div className={styles.productList}>
          <Typography className={styles.label} tagStyle="bodyMedium">
            {t('items.search.priceDetails.priceIncludes')}
          </Typography>

          {includedProducts.map((product, productIndex) => (
            <div key={productIndex} className={styles.product}>
              <Typography
                className={classNames(styles.body, styles.text)}
                tagStyle="bodyMedium"
              >
                {product.text}
              </Typography>

              <Typography
                className={classNames(styles.value, styles.text)}
                tagStyle="bodyMediumBold"
              >
                {product.value}
              </Typography>
            </div>
          ))}
        </div>

        <div className={styles.prices}>
          {roomPrices.map((roomPrice, roomPriceIndex) => (
            <div key={roomPriceIndex} className={styles.room}>
              <Typography
                className={styles.priceHeadline}
                tagStyle="bodyMediumBold"
              >
                {roomPrice.roomTypeName}
              </Typography>

              {roomPrice.lines.map((line, lineIndex) => (
                <div key={lineIndex} className={styles.priceRow}>
                  <Typography className={styles.text} tagStyle="bodyMedium">
                    {line.text}
                  </Typography>

                  <Typography className={styles.text} tagStyle="bodyMedium">
                    {`${line.count}x`}
                  </Typography>

                  <Typography
                    className={classNames(styles.text, styles.value, {
                      [styles.isDiscount]: line.type === 'discount',
                      [styles.isNormal]: line.type === 'normalPrice',
                    })}
                    tagStyle="bodyMediumBold"
                  >
                    {currencyFormatter(line.price)}
                  </Typography>
                </div>
              ))}
            </div>
          ))}
        </div>

        <div className={styles.product}>
          <Typography className={styles.text} tagStyle="bodyMedium">
            {t('items.search.priceDetails.totalPrice')}
          </Typography>

          <Typography
            className={classNames(styles.text, styles.value)}
            tagStyle="headingSmall"
          >
            {currencyFormatter(totalPrice)}
          </Typography>
        </div>

        {url && (
          <Button
            className={styles.button}
            text={t('items.search.priceDetails.orderNow')}
            url={url}
            variant="primary"
          />
        )}
      </div>
    </Lightbox>
  );
};

PriceDetails.displayName = 'PriceDetails';

export default PriceDetails;
